import type { MouseEvent } from 'react';
import { useCallback } from 'react';
import type { BaseKey } from '@refinedev/core';
import { useNavigation, useTranslate } from '@refinedev/core';
import type { TableProps } from 'antd';

export type TableRowNavigateOptions<T> = {
  idKey?: keyof T & string;
};

function readRecordId<T>(
  record: T,
  idKey: keyof T | 'id'
): BaseKey | undefined {
  const v = (record as Record<string, unknown>)[idKey as string];
  return v as BaseKey | undefined;
}

/**
 * Wraps Ant Design `Table` props so a row click navigates to Refine **show** for that record.
 * Clicks on links, buttons, inputs, etc. are ignored so row actions (e.g. EditButton) keep working.
 */
export function useTableRowNavigateToShow() {
  const { show } = useNavigation();
  const t = useTranslate();

  return useCallback(
    <T>(
      resource: string | ((record: T) => string),
      tableProps: TableProps<T>,
      options?: TableRowNavigateOptions<T>
    ): TableProps<T> => {
      const idKey = (options?.idKey ?? 'id') as keyof T | 'id';
      const prevOnRow = tableProps.onRow;
      const resolveResource = (record: T) =>
        typeof resource === 'function' ? resource(record) : resource;

      return {
        ...tableProps,
        pagination:
          tableProps.pagination === false
            ? false
            : {
                ...tableProps.pagination,
                showTotal: (total, range) =>
                  t('common.pagination_total', {
                    from: range[0],
                    to: range[1],
                    total,
                  }),
              },
        onRow: (record, index) => {
          const base =
            typeof prevOnRow === 'function'
              ? (prevOnRow(record, index) ?? {})
              : {};
          return {
            ...base,
            onClick: (e: MouseEvent) => {
              base.onClick?.(e);
              const el = e.target;
              if (!(el instanceof Element)) return;
              if (
                el.closest(
                  'a,button,input,textarea,select,label,[role="button"],[data-row-nav-ignore],.ant-table-selection-column'
                )
              ) {
                return;
              }
              const rid = readRecordId(record, idKey);
              if (rid === undefined || rid === null || rid === '') return;
              show(resolveResource(record), rid);
            },
            style: { cursor: 'pointer', ...base.style },
          };
        },
      };
    },
    [show, t]
  );
}
